import React, { Component, ErrorInfo, ReactNode } from 'react';
import { posthog } from '../../services/posthog';
import { RotateCcw, AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Send to PostHog
    posthog.capture('exception', {
      error: error.name,
      message: error.message,
      stack: errorInfo.componentStack,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-red-100">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Đã có lỗi xảy ra
            </h1>

            <p className="text-gray-600 mb-8">
              Rất tiếc, ứng dụng đã gặp sự cố không mong muốn. Đội ngũ kỹ thuật đã được thông báo về vấn đề này.
            </p>

            <button
              onClick={this.handleReload}
              className="w-full inline-flex items-center justify-center px-4 py-3 bg-indigo-600 border border-transparent rounded-xl text-white font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all shadow-md active:scale-95"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Tải lại trang
            </button>

            {/* Optional: Show error details in dev mode */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mt-8 text-left">
                <p className="text-xs font-mono text-red-500 bg-red-50 p-3 rounded-lg break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
